import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Star,
  Play,
  Clock,
  Users,
  BookOpen,
  Award,
  Globe,
  CheckCircle,
  FileText,
  HelpCircle,
  Lock,
  PlayCircle,
  Heart,
  Share2,
  ChevronRight,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CourseDetailPage = ({ user }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const isMasterminder = user?.membership_duration === 'masterminder';

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/courses/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setCourse(data);
          
          // Check enrollment status
          if (user) {
            const enrollRes = await fetch(`${BACKEND_URL}/api/enrollments/me`, { credentials: 'include' });
            if (enrollRes.ok) {
              const enrollments = await enrollRes.json();
              const enrolled = enrollments.find(e => e.course.id === data.id);
              if (enrolled) setIsEnrolled(true);
            }
          }

          // Fetch reviews
          const reviewsResponse = await fetch(`${BACKEND_URL}/api/reviews/${data.id}`);
          if (reviewsResponse.ok) {
            const reviewsData = await reviewsResponse.json();
            setReviews(reviewsData.reviews || []);
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourse();
  }, [slug, user]);

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Masterminders bypass payment - auto-enroll directly
    if (isMasterminder || course.price === 0) {
      setEnrolling(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/enrollments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ course_id: course.id })
        });
        
        if (response.ok) {
          navigate(`/learn/${course.slug}`);
        } else {
          const error = await response.json();
          if (error.detail === 'Already enrolled in this course') {
            navigate(`/learn/${course.slug}`);
          } else {
            alert(error.detail || 'Failed to enroll');
          }
        }
      } catch (error) {
        console.error('Enrollment error:', error);
        alert('Failed to enroll. Please try again.');
      } finally {
        setEnrolling(false);
      }
      return;
    }
    
    // For paid courses (non-masterminder), redirect to checkout page
    navigate(`/checkout/${course.slug}`);
  };

  const handlePlayVideo = (lesson) => {
    if (lesson.type === 'video') {
      setCurrentVideo(lesson);
      setShowVideoModal(true);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  };

  if (!course || loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading course...</p>
        </div>
      </div>
    );
  }

  const courseReviews = reviews;
  const totalCompletedLessons = course.sections?.reduce(
    (acc, section) => acc + (section.lessons?.filter((l) => l.completed)?.length || 0),
    0
  ) || 0;
  const totalLessons = course.sections?.reduce(
    (acc, section) => acc + (section.lessons?.length || 0),
    0
  ) || course.total_lessons;
  const progressPercent = totalLessons > 0 ? (totalCompletedLessons / totalLessons) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-950 pt-16">
      {/* Course Header */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-950 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                  {course.category}
                </Badge>
                <Badge variant="secondary" className="bg-gray-800 text-gray-300">
                  {course.level}
                </Badge>
                {course.featured && (
                  <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                    Bestseller
                  </Badge>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {course.title}
              </h1>

              <p className="text-gray-400 text-lg mb-6">{course.description}</p>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-white font-semibold">{course.rating}</span>
                  <span className="text-gray-400">({course.reviews_count || 0} reviews)</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Users className="w-5 h-5" />
                  <span>{(course.total_students || 0).toLocaleString()} students</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Clock className="w-5 h-5" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Globe className="w-5 h-5" />
                  <span>{course.language}</span>
                </div>
              </div>
            </div>

            {/* Course Card */}
            <div className="lg:col-span-1">
              <Card className="bg-gray-900 border-gray-800 sticky top-24 overflow-hidden">
                <div className="aspect-video relative">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <button
                      onClick={() => handlePlayVideo(course.sections?.[0]?.lessons?.[0])}
                      className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors group"
                    >
                      <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                  <p className="absolute bottom-3 left-3 text-white text-sm">Preview this course</p>
                </div>

                <CardContent className="p-6">
                  {isMasterminder ? (
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="text-lg text-gray-500 line-through">${course.price}</span>
                      <span className="text-xl font-bold text-amber-400">Included</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="text-3xl font-bold text-white">${course.price}</span>
                      {course.original_price > course.price && (
                        <>
                          <span className="text-lg text-gray-500 line-through">
                            ${course.original_price}
                          </span>
                          <Badge className="bg-emerald-500/10 text-emerald-400">
                            {Math.round(((course.original_price - course.price) / course.original_price) * 100)}% off
                          </Badge>
                        </>
                      )}
                    </div>
                  )}

                  {course.available_soon ? (
                    <div className="mb-4">
                      <div className="w-full py-3 px-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
                        <p className="text-amber-400 font-semibold text-lg mb-1">Available Soon</p>
                        <p className="text-gray-400 text-sm">This course is coming soon. Stay tuned!</p>
                      </div>
                    </div>
                  ) : isEnrolled ? (
                    <>
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-400">Your progress</span>
                          <span className="text-white">{Math.round(progressPercent)}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-500 mb-3"
                        onClick={() => navigate(`/learn/${course.slug}`)}
                      >
                        Continue Learning
                      </Button>
                    </>
                  ) : isMasterminder ? (
                    <>
                      <div className="mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-amber-400 text-xs font-semibold text-center">Masterminder Access</p>
                      </div>
                      <Button
                        className="w-full mb-4 bg-amber-600 hover:bg-amber-500"
                        onClick={handleEnroll}
                        disabled={enrolling}
                        data-testid="masterminder-enroll-btn"
                      >
                        {enrolling ? 'Enrolling...' : 'Enrol Courses'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      className={`w-full mb-4 ${course.price > 0 ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                      onClick={handleEnroll}
                      data-testid="enroll-button"
                    >
                      {course.price > 0 ? `Enroll Now - $${course.price}` : 'Start Learning - Free Access'}
                    </Button>
                  )}

                  <div className="flex items-center justify-center gap-4 mb-6">
                    <button
                      onClick={() => setIsWishlisted(!isWishlisted)}
                      className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
                    >
                      <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                      <span className="text-sm">Wishlist</span>
                    </button>
                    <button className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors">
                      <Share2 className="w-5 h-5" />
                      <span className="text-sm">Share</span>
                    </button>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>{totalLessons} lessons</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>{course.duration} of content</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>Full lifetime access</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>Certificate of completion</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>30-day money-back guarantee</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-gray-900 border border-gray-800 p-1">
                <TabsTrigger value="overview" className="data-[state=active]:bg-gray-800">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="curriculum" className="data-[state=active]:bg-gray-800">
                  Curriculum
                </TabsTrigger>
                <TabsTrigger value="reviews" className="data-[state=active]:bg-gray-800">
                  Reviews
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-6">
                <div className="space-y-8">
                  {/* What you will learn */}
                  <Card className="bg-gray-900/50 border-gray-800">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-white mb-4">What you&apos;ll learn</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {course.what_you_will_learn?.map((item, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                            <span className="text-gray-300">{item}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Requirements */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Requirements</h3>
                    <ul className="space-y-2">
                      {course.requirements?.map((req, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-300">
                          <ChevronRight className="w-5 h-5 text-gray-500 shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Description</h3>
                    <p className="text-gray-400 leading-relaxed">{course.description}</p>
                  </div>
                </div>
              </TabsContent>

              {/* Curriculum Tab */}
              <TabsContent value="curriculum" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-gray-400">{course.sections?.length || 0} sections</span>
                    <span className="text-gray-600 mx-2">•</span>
                    <span className="text-gray-400">{totalLessons} lessons</span>
                    <span className="text-gray-600 mx-2">•</span>
                    <span className="text-gray-400">{course.duration} total</span>
                  </div>
                </div>

                <Accordion type="multiple" className="space-y-3">
                  {course.sections?.map((section, sectionIndex) => (
                    <AccordionItem
                      key={section.id}
                      value={section.id}
                      className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-800/50">
                        <div className="flex items-center gap-3 text-left">
                          <span className="text-white font-medium">
                            Section {sectionIndex + 1}: {section.title}
                          </span>
                          <span className="text-sm text-gray-500">
                            {section.lessons?.length || 0} lessons
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-0">
                        <div className="border-t border-gray-800">
                          {section.lessons?.map((lesson, lessonIndex) => (
                            <div
                              key={lesson.id}
                              className="flex items-center justify-between px-4 py-3 hover:bg-gray-800/30 cursor-pointer border-b border-gray-800/50 last:border-0"
                              onClick={() => lesson.type === 'video' && handlePlayVideo(lesson)}
                            >
                              <div className="flex items-center gap-3">
                                {lesson.type === 'video' ? (
                                  <PlayCircle className="w-5 h-5 text-gray-500" />
                                ) : lesson.type === 'quiz' ? (
                                  <HelpCircle className="w-5 h-5 text-gray-500" />
                                ) : (
                                  <FileText className="w-5 h-5 text-gray-500" />
                                )}
                                <span className={`${lesson.completed ? 'text-emerald-400' : 'text-gray-300'}`}>
                                  {lesson.title}
                                </span>
                                {lesson.completed && (
                                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {lesson.duration && (
                                  <span className="text-sm text-gray-500">{lesson.duration}</span>
                                )}
                                {lessonIndex > 1 && !isEnrolled && (
                                  <Lock className="w-4 h-4 text-gray-600" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="mt-6">
                <div className="space-y-6">
                  {/* Rating Summary */}
                  <Card className="bg-gray-900/50 border-gray-800">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <p className="text-5xl font-bold text-white mb-1">{course.rating}</p>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${
                                  i < Math.floor(course.rating)
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-gray-400 text-sm">{course.reviews_count || 0} reviews</p>
                        </div>
                        <div className="flex-1 space-y-2">
                          {[5, 4, 3, 2, 1].map((stars) => (
                            <div key={stars} className="flex items-center gap-2">
                              <span className="text-sm text-gray-400 w-12">{stars} stars</span>
                              <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-yellow-400"
                                  style={{
                                    width: `${stars === 5 ? 70 : stars === 4 ? 20 : stars === 3 ? 7 : 3}%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-500 w-10">
                                {stars === 5 ? '70%' : stars === 4 ? '20%' : stars === 3 ? '7%' : '3%'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Reviews List */}
                  <div className="space-y-4">
                    {courseReviews.map((review) => (
                      <Card key={review.id} className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={review.user_avatar} />
                              <AvatarFallback>{review.user_name?.charAt(0) || 'U'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-white font-medium">{review.user_name}</p>
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center">
                                      {[...Array(5)].map((_, i) => (
                                        <Star
                                          key={i}
                                          className={`w-4 h-4 ${
                                            i < review.rating
                                              ? 'text-yellow-400 fill-yellow-400'
                                              : 'text-gray-600'
                                          }`}
                                        />
                                      ))}
                                    </div>
                                    <span className="text-gray-500 text-sm">{review.created_at}</span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-gray-300">{review.content}</p>
                              <button className="text-sm text-gray-500 hover:text-white mt-3">
                                Helpful ({review.helpful})
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white">{currentVideo?.title}</DialogTitle>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </DialogHeader>
          <div className="aspect-video">
            {currentVideo && (
              <iframe
                src={getYouTubeEmbedUrl(currentVideo.videoUrl)}
                title={currentVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseDetailPage;
